import axios from 'axios'
import { baseURL } from '../api/axiosFun'

const TOKEN_KEY = 'logintoken'
const DEFAULT_REPORT_NAME = 'asset-evaluation-report.docx'

function getAuthorizationHeader() {
  const token = localStorage.getItem(TOKEN_KEY) || ''

  if (!token) {
    return {}
  }

  return {
    Authorization: /^Bearer\s/i.test(token) ? token : ('Bearer ' + token)
  }
}

function trimTrailingSlash(value) {
  return String(value || '').replace(/\/+$/, '')
}

export function toAbsoluteDownloadUrl(url) {
  if (!url || typeof url !== 'string') return ''
  if (/^https?:\/\//i.test(url)) return url

  const normalizedPath = url.charAt(0) === '/' ? url : ('/' + url)
  const normalizedBaseUrl = trimTrailingSlash(baseURL)

  if (normalizedBaseUrl) {
    return normalizedBaseUrl + normalizedPath
  }

  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin + normalizedPath
  }

  return url
}

function decodeFileName(value) {
  if (!value) return ''

  try {
    return decodeURIComponent(value)
  } catch (error) {
    return value
  }
}

function extractFileNameFromHeaders(headers) {
  const disposition = headers && (headers['content-disposition'] || headers['Content-Disposition'])

  if (!disposition) return ''

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)

  if (utf8Match && utf8Match[1]) {
    return decodeFileName(utf8Match[1].trim().replace(/^"|"$/g, ''))
  }

  const plainMatch = disposition.match(/filename=([^;]+)/i)

  if (plainMatch && plainMatch[1]) {
    return decodeFileName(plainMatch[1].trim().replace(/^"|"$/g, ''))
  }

  return ''
}

function extractFileNameFromUrl(url) {
  if (!url || typeof url !== 'string') return ''

  const normalized = url.split('?')[0].split('#')[0]
  const segments = normalized.split('/')
  return decodeFileName(segments[segments.length - 1] || '')
}

function normalizeDownloadName(fileName) {
  if (!fileName || typeof fileName !== 'string') {
    return DEFAULT_REPORT_NAME
  }

  const normalized = fileName.trim()
  return normalized || DEFAULT_REPORT_NAME
}

function isCrossOriginUrl(url) {
  if (typeof window === 'undefined' || !window.location || !url) {
    return false
  }

  try {
    return new window.URL(url, window.location.origin).origin !== window.location.origin
  } catch (error) {
    return false
  }
}

function triggerAnchorDownload(url, fileName, options) {
  const settings = Object.assign({
    target: '',
    noReferrer: false,
    forceDownload: true
  }, options)
  const link = document.createElement('a')
  link.href = url
  link.style.display = 'none'
  link.rel = settings.noReferrer ? 'noreferrer noopener' : 'noopener'
  link.referrerPolicy = settings.noReferrer ? 'no-referrer' : 'strict-origin-when-cross-origin'

  if (settings.target) {
    link.target = settings.target
  }

  if (settings.forceDownload && fileName) {
    link.download = fileName
  }

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export async function downloadRemoteFile(url, options) {
  const settings = Object.assign({
    fileName: ''
  }, options)
  const absoluteUrl = toAbsoluteDownloadUrl(url)
  const fallbackName = normalizeDownloadName(settings.fileName || extractFileNameFromUrl(absoluteUrl))

  if (isCrossOriginUrl(absoluteUrl)) {
    triggerAnchorDownload(absoluteUrl, fallbackName, {
      target: '_blank',
      noReferrer: true,
      forceDownload: false
    })
    return true
  }

  try {
    const response = await axios({
      method: 'get',
      url: absoluteUrl,
      headers: getAuthorizationHeader(),
      responseType: 'blob'
    })

    const resolvedName = normalizeDownloadName(extractFileNameFromHeaders(response.headers) || fallbackName)
    const blobUrl = window.URL.createObjectURL(response.data)

    triggerAnchorDownload(blobUrl, resolvedName)
    window.setTimeout(function () {
      window.URL.revokeObjectURL(blobUrl)
    }, 1000)
    return true
  } catch (error) {
    triggerAnchorDownload(absoluteUrl, fallbackName, {
      target: '_blank',
      noReferrer: true,
      forceDownload: false
    })
    return false
  }
}



// WEBPACK FOOTER //
// ./src/utils/download.js